/**
 * Layout of a photo inside a fixed mask/box (cover + zoom + pan).
 * offsetX/Y are −1…1 relative to max pan at the current scale.
 */
export function maskPhotoLayout(
  boxW: number,
  boxH: number,
  naturalW: number,
  naturalH: number,
  maskScale = 1,
  offsetX = 0,
  offsetY = 0,
): { x: number; y: number; width: number; height: number } {
  const nw = Math.max(1, naturalW);
  const nh = Math.max(1, naturalH);
  const scale = Math.max(1, maskScale);
  const cover = Math.max(boxW / nw, boxH / nh);
  const width = nw * cover * scale;
  const height = nh * cover * scale;
  const maxPanX = Math.max(0, (width - boxW) / 2);
  const maxPanY = Math.max(0, (height - boxH) / 2);
  const x = (boxW - width) / 2 + Math.max(-1, Math.min(1, offsetX)) * maxPanX;
  const y = (boxH - height) / 2 + Math.max(-1, Math.min(1, offsetY)) * maxPanY;
  return { x, y, width, height };
}

/**
 * Mask PNGs are authored 1:1 (same as Edit Image `.ox-mask-fit-stage`).
 * Returns a centered square inside an arbitrary placement box.
 */
export function squareMaskBox(
  width: number,
  height: number,
): { side: number; offsetX: number; offsetY: number } {
  const side = Math.min(Math.max(1, width), Math.max(1, height));
  return {
    side,
    offsetX: (width - side) / 2,
    offsetY: (height - side) / 2,
  };
}

/**
 * When a shape mask is active, object placement must be square so the mask
 * is not stretched to the photo’s natural aspect ratio.
 */
export function maskViewportSizeMm(
  widthMm: number,
  heightMm: number,
  mask: string | null | undefined,
): { widthMm: number; heightMm: number } {
  if (!mask || mask === 'none') return { widthMm, heightMm };
  const side = Math.min(widthMm, heightMm);
  return { widthMm: side, heightMm: side };
}
